// The alarm sound: a short beep made by Web Audio, no files (STAGE_4 4.5). Browsers keep
// audio shut until the page has had a click or a key press, so the context is created
// suspended and opened by the first gesture - see useAlarmSound.

type AudioContextClass = typeof AudioContext;

let context: AudioContext | null = null;

function contextClass(): AudioContextClass | undefined {
  // Either may be missing: an old Safari has only the prefixed one, a test page neither.
  const scope = window as unknown as {
    AudioContext?: AudioContextClass;
    webkitAudioContext?: AudioContextClass;
  };
  return scope.AudioContext ?? scope.webkitAudioContext;
}

export function audioContext(): AudioContext | null {
  if (context) return context;
  const Class = contextClass();
  if (!Class) return null;
  try {
    context = new Class();
  } catch {
    return null;
  }
  return context;
}

/** Whether a beep would actually be heard now: false until a gesture has opened audio. */
export function audioReady(): boolean {
  return audioContext()?.state === 'running';
}

/** Open audio; call it from inside a click or key handler. */
export async function unlockAudio(): Promise<boolean> {
  const ctx = audioContext();
  if (!ctx) return false;
  try {
    if (ctx.state !== 'running') await ctx.resume();
  } catch {
    return false;
  }
  return ctx.state === 'running';
}

/** Two short tones, 880 then 660 Hz: distinct from anything else a PC says. */
export function beep(): void {
  const ctx = audioContext();
  if (!ctx || ctx.state !== 'running') return;
  const start = ctx.currentTime;
  [880, 660].forEach((frequency, index) => {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.frequency.value = frequency;
    gain.gain.value = 0.12;
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start(start + index * 0.2);
    oscillator.stop(start + index * 0.2 + 0.16);
  });
}
